import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import opportunity components (lazy-loaded for performance)
const OpportunitiesList = React.lazy(() => import("./OpportunitiesList"));
const NewOpportunity = React.lazy(() => import("./NewOpportunity"));
const OpportunityDetail = React.lazy(() => import("./OpportunityDetail"));
const OpportunitiesWhatsNew = React.lazy(() => import("./OpportunitiesWhatsNew"));
const OpportunityTypes = React.lazy(() => import("./OpportunityTypes"));

/**
 * Opportunities Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (opportunities/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/opportunities/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const opportunitiesModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "opportunities",
    name: "Opportunities",
    description: "Track sales opportunities and manage your pipeline with integrated dashboard and payment tracking",
    version: "1.2.1",
    enabled: true,
    category: "sales",
    order: 3,
    icon: "💰",
    color: "#3182CE",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/opportunities/new",
            component: NewOpportunity,
            permissionLevel: "create",
        },
        {
            path: "/opportunities/types",
            component: OpportunityTypes,
            permissionLevel: "view",
        },
        {
            path: "/opportunities/whats-new",
            component: OpportunitiesWhatsNew,
            permissionLevel: "view",
        },
        {
            path: "/opportunities/:id",
            component: OpportunityDetail,
            permissionLevel: "view",
        },
        {
            path: "/opportunities",
            component: OpportunitiesList,
            permissionLevel: "view",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Opportunities",
            path: "/opportunities",
            icon: "💰",
            permissionLevel: "view",
        },
        {
            label: "New Opportunity",
            path: "/opportunities/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Opportunity Types",
            path: "/opportunities/types",
            icon: "🏷️",
            permissionLevel: "view",
        },
        {
            label: "What's New",
            path: "/opportunities/whats-new",
            icon: "🎉",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/opportunities/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        receive_reports: [],
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Add Opportunity",
            path: "/opportunities/new",
            icon: "➕",
            description: "Create a new sales opportunity"
        },
        {
            label: "View Pipeline",
            path: "/opportunities",
            icon: "📊",
            description: "View your sales pipeline and dashboard"
        }
    ],

    // 📋 CHANGELOG
    changelog: [
        {
            version: "1.2.1",
            releaseDate: "2025-11-25",
            title: "Pipeline Metrics & Revenue Calculation Fixes",
            features: [
                "✅ Fixed Total Pipeline Value to include delivery stage opportunities with unpaid payments",
                "✅ Fixed Expected Revenue calculation to only count unpaid scheduled payments",
                "✅ Fixed double-counting of one-off value when scheduled payments exist",
                "✅ Applied probability weighting to unpaid scheduled payments",
                "✅ Updated metric descriptions to clarify delivery stage inclusion",
                "✅ Fixed opportunity detail page expected revenue display (removed duplicate dollar sign)",
                "✅ All revenue calculations now properly filter out paid payments",
                "✅ Active opportunities count now includes WON opportunities with unpaid payments"
            ],
            notes: "Critical fixes to pipeline value and revenue calculations to accurately track opportunities in delivery stage with pending payments and prevent double-counting."
        },
        {
            version: "1.2.0",
            releaseDate: "2025-11-22",
            title: "Advanced Payment & Commission Tracking",
            features: [
                "✅ Complete payment schedule member split management",
                "✅ Individual payment status tracking (Pending → Received → Distributed → Cancelled)",
                "✅ Flexible payout scheduling (days after payment or specific date)",
                "✅ Payout status tracking per team member (Pending, Scheduled, Processing, Paid)",
                "✅ Live calculation of total earned and pending payouts",
                "✅ Payment Schedule Tracking section with purple badge styling",
                "✅ Enhanced team member tooltips (Total Earned and Pending Payout)",
                "✅ Automatic payment amount splitting across team members",
                "✅ Unallocated company revenue tracking",
                "✅ Payment notes and transaction tracking",
                "✅ Real-time split percentage validation (must total 100%)",
                "✅ Improved GraphQL type safety for payment mutations",
                "✅ Cleaner data handling with proper field filtering"
            ],
            notes: "Major enhancement to payment and commission tracking with complete lifecycle management from client payment receipt through team member payout distribution."
        },
        {
            version: "1.1.0",
            releaseDate: "2024-08-10",
            title: "Enhanced Pipeline Dashboard & Payment Tracking",
            features: [
                "✅ Integrated dashboard view at top of opportunities page",
                "✅ Added Delivery and Delivered stages for post-sale tracking",
                "✅ Renamed Finished stage to Delivered for clarity",
                "✅ Payment schedule display on opportunity cards",
                "✅ Subscription/recurring payment display on cards",
                "✅ Fixed conversion funnel to show all opportunities (including Won/Lost)",
                "✅ Added metric explanations (hover tooltips and descriptions)",
                "✅ Total Pipeline Value calculation and display",
                "✅ Expected Revenue with probability weighting (includes subscriptions)",
                "✅ Conversion Rate tracking (Won ÷ Total Closed)",
                "✅ Average Deal Size and Sales Cycle metrics",
                "✅ Revenue targets tracking (Monthly, Quarterly, Yearly)",
                "✅ Pipeline health score indicator",
                "✅ Conversion funnel visualization",
                "✅ Suggested actions based on pipeline health",
                "✅ Task suggestions for moving deals forward"
            ],
            notes: "Major dashboard improvements with payment tracking and enhanced pipeline stages for complete sales cycle management from lead to delivery completion."
        },
        {
            version: "1.0.0",
            releaseDate: "2023-06-12",
            title: "Initial Opportunities Module Release",
            features: [
                "✅ Kanban-style pipeline view with drag & drop",
                "✅ Multiple pipeline stages (Lead, Qualified, Proposal, Negotiation, Closed Won/Lost)",
                "✅ Opportunity creation and editing",
                "✅ Client association and contact information",
                "✅ Value tracking and probability estimation",
                "✅ Expected close date tracking",
                "✅ Priority levels (Low, Medium, High, Critical)",
                "✅ Task management per opportunity",
                "✅ Notes and activity tracking",
                "✅ Team member assignment",
                "✅ Source tracking",
                "✅ Search and filter capabilities",
                "✅ Stats cards (Active, Won, Lost, Win Rate, Tasks)",
                "✅ Stage-based organization and visualization",
                "✅ Mobile-responsive design"
            ],
            notes: "First production release of the opportunities module with complete CRM pipeline functionality."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: [
        {
            title: "Advanced Payment Features",
            description: "Enhanced payment tracking and revenue management",
            targetVersion: "1.2.0",
            priority: "high",
            status: "planned",
            features: [
                "Payment schedule management UI (add, edit, mark paid)",
                "Subscription management interface",
                "Automatic payment reminders",
                "Payment history and audit trail",
                "Revenue recognition tracking",
                "Invoice generation from opportunities",
                "Payment method tracking (credit card, ACH, wire, etc.)",
                "Multi-currency support for international deals"
            ]
        },
        {
            title: "Team Collaboration",
            description: "Enhanced features for sales team collaboration",
            targetVersion: "1.3.0",
            priority: "high",
            status: "planned",
            features: [
                "Opportunity sharing and permissions",
                "Team activity feed",
                "@mentions in notes and comments",
                "Opportunity transfer between team members",
                "Sales team leaderboards",
                "Collaborative deal reviews",
                "Territory management",
                "Commission split calculations"
            ]
        },
        {
            title: "Automation & Workflows",
            description: "Automated actions and intelligent workflows",
            targetVersion: "1.4.0",
            priority: "medium",
            status: "planned",
            features: [
                "Auto-assignment rules based on criteria",
                "Stage automation triggers (e.g., auto-create tasks)",
                "Email sequences for different stages",
                "SMS notifications for opportunity updates",
                "Slack/Teams integration for alerts",
                "Automatic opportunity scoring",
                "Win/Loss analysis automation",
                "Stale opportunity detection and alerts"
            ]
        },
        {
            title: "Advanced Analytics",
            description: "Deep insights into sales performance",
            targetVersion: "1.5.0",
            priority: "medium",
            status: "planned",
            features: [
                "Sales forecasting based on pipeline",
                "Win/Loss analysis reports",
                "Time-in-stage analytics",
                "Source performance tracking",
                "Team performance comparisons",
                "Custom report builder",
                "Export to Excel/CSV",
                "Dashboard widgets and visualizations",
                "Predictive analytics for deal outcomes"
            ]
        },
        {
            title: "Integration Ecosystem",
            description: "Connect with external tools and systems",
            targetVersion: "1.6.0",
            priority: "low",
            status: "planned",
            features: [
                "Email sync (Gmail, Outlook) for opportunity communication",
                "Calendar integration for meetings",
                "Document attachment from Google Drive/Dropbox",
                "CRM import/export (Salesforce, HubSpot format)",
                "Zapier integration for workflow automation",
                "Webhook support for custom integrations",
                "API for third-party developers",
                "LinkedIn Sales Navigator integration"
            ]
        },
        {
            title: "AI-Powered Features",
            description: "Intelligent assistance for sales teams",
            targetVersion: "2.0.0",
            priority: "low",
            status: "planned",
            features: [
                "AI-powered deal insights and recommendations",
                "Smart next action suggestions",
                "Automated email composition",
                "Meeting notes transcription and summary",
                "Sentiment analysis on communications",
                "Win probability prediction",
                "Optimal contact time suggestions",
                "Natural language search and queries"
            ]
        }
    ]
};

export default opportunitiesModuleConfig;